import { prefersReducedMotion } from '@theme/utilities';
import { getScrollContainer, scrollTo } from '@theme/scroll-container';

const ANCHOR_CLASS = 'group-block__anchor';
const PENDING_KEY = 'group-anchor-pending';
const RESERVED_HASHES = new Set([
  'maincontent',
  'header-component',
  'header-group',
  'a11y-new-window-message',
]);

/**
 * Finds the group for a custom anchor hash. Ignores Shopify block/section IDs.
 * @param {string} id
 * @returns {HTMLElement | null}
 */
function findGroupForHash(id) {
  if (!id) return null;

  const normalized = id.toLowerCase();
  const anchors = document.querySelectorAll(`.${ANCHOR_CLASS}[data-group-anchor]`);

  for (const anchor of anchors) {
    const customId = anchor.getAttribute('data-group-anchor') || anchor.id;
    if (customId === id || customId.toLowerCase() === normalized) {
      return anchor.closest('.group-block') || anchor;
    }
  }

  return null;
}

/**
 * @param {string} id
 * @returns {boolean}
 */
function isReservedHash(id) {
  const normalized = id.toLowerCase();
  return RESERVED_HASHES.has(normalized) || normalized.startsWith('shopify-');
}

/**
 * @param {string} path
 * @returns {string}
 */
function normalizePath(path) {
  if (!path || path === '/') return '/';
  return path.replace(/\/+$/, '') || '/';
}

/**
 * @returns {string}
 */
function rootPath() {
  return normalizePath(window.Shopify?.routes?.root || '/');
}

/**
 * @param {string} path
 * @param {string} id
 * @returns {string}
 */
function pathWithHash(path, id) {
  const normalized = normalizePath(path);
  const prefix = normalized === '/' ? '' : normalized;
  return `${prefix}/#${id}`;
}

/**
 * @param {string} href
 * @returns {string}
 */
function hashId(href) {
  try {
    return decodeURIComponent(new URL(href, window.location.href).hash.replace(/^#/, ''));
  } catch {
    return '';
  }
}

/**
 * Builds the page + hash destination for a custom-anchor link.
 * Hash-only `#about-us` and `/#about-us` go to the homepage.
 * `/page-url#about-us` keeps that page path.
 * @param {string} href
 * @param {string} id
 * @returns {string | null}
 */
function destinationForHashLink(href, id) {
  const raw = href.trim();
  if (raw.startsWith('#')) {
    return pathWithHash(rootPath(), id);
  }

  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin || !url.hash) return null;
    return pathWithHash(url.pathname, id);
  } catch {
    return null;
  }
}

/**
 * @param {HTMLElement} target
 * @returns {number}
 */
function scrollOffset(target) {
  const fromElement = parseFloat(getComputedStyle(target).scrollMarginBlockStart);
  if (Number.isFinite(fromElement) && fromElement > 0) return fromElement;

  const root = getComputedStyle(document.documentElement);
  return (
    parseFloat(root.getPropertyValue('--header-group-height')) ||
    parseFloat(root.getPropertyValue('--header-height')) ||
    0
  );
}

/**
 * @param {string} id
 * @returns {boolean}
 */
function scrollToGroupAnchor(id) {
  const target = findGroupForHash(id);
  if (!target) return false;

  const container = getScrollContainer();
  const isDocumentScroller =
    container === document.scrollingElement ||
    container === document.documentElement ||
    container === document.body;
  const containerTop = isDocumentScroller ? 0 : container.getBoundingClientRect().top;
  const top = target.getBoundingClientRect().top - containerTop + container.scrollTop - scrollOffset(target);

  scrollTo({
    top: Math.max(0, top),
    behavior: prefersReducedMotion() ? 'instant' : 'smooth',
  });

  return true;
}

function pendingId() {
  try {
    return sessionStorage.getItem(PENDING_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * @param {string} id
 */
function storePendingId(id) {
  try {
    sessionStorage.setItem(PENDING_KEY, id);
  } catch {
    /* sessionStorage can be blocked */
  }
}

function clearPendingId() {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* sessionStorage can be blocked */
  }
}

function consumePendingAndScroll() {
  const id = pendingId() || decodeURIComponent(window.location.hash.replace(/^#/, ''));
  if (!id || isReservedHash(id)) return;

  if (scrollToGroupAnchor(id)) {
    clearPendingId();
  }
}

document.addEventListener('click', (event) => {
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!link || (link.target && link.target !== '_self')) return;

  const href = link.getAttribute('href');
  if (!href) return;

  const id = hashId(href);
  if (!id || isReservedHash(id)) return;

  if (findGroupForHash(id)) {
    event.preventDefault();
    if (window.location.hash !== `#${id}`) {
      history.pushState(history.state, '', `#${id}`);
    }
    requestAnimationFrame(() => scrollToGroupAnchor(id));
    return;
  }

  const destination = destinationForHashLink(href, id);
  if (!destination) return;

  const next = new URL(destination, window.location.href);
  if (normalizePath(next.pathname) === normalizePath(window.location.pathname)) return;

  event.preventDefault();
  storePendingId(id);
  window.location.assign(destination);
});

function scheduleScrollAttempts() {
  consumePendingAndScroll();
  requestAnimationFrame(() => requestAnimationFrame(consumePendingAndScroll));
  [100, 300, 700].forEach((delay) => {
    setTimeout(consumePendingAndScroll, delay);
  });
}

window.addEventListener('hashchange', consumePendingAndScroll);
window.addEventListener('pagereveal', scheduleScrollAttempts);
window.addEventListener('pageshow', scheduleScrollAttempts);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleScrollAttempts);
} else {
  scheduleScrollAttempts();
}
